import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import { AltRoute, Add, Delete, Save, Cancel } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { PaymentRailSettingsApi } from '@/api/clients/payment-rail-settings/payment-rail-settings.api';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';
import CpdActionButton from '@/components/ui/CpdActionButton';
import useFormValidation from '@/hooks/useFormValidation';
import PaymentRailsSection from './PaymentRailsSection';

const EMPTY_FORM = {
  name: '',
  is_disabled: false,
  identifiers: [''],
};

export default function PaymentRailSettingFormDialog({
  open,
  organizationId,
  settingId,
  onClose,
  onSaved,
}) {
  const { t } = useTranslation();
  const isEdit = !!settingId;
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    clearAllErrors,
  } = useFormValidation();

  useEffect(() => {
    if (!open) return;
    clearAllErrors();
    if (!isEdit) {
      setForm(EMPTY_FORM);
      return;
    }
    let cancelled = false;
    setFetching(true);
    PaymentRailSettingsApi.get(organizationId, settingId)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || {};
        setForm({
          name: data.name || '',
          is_disabled: !!data.isDisabled,
          identifiers: data.paymentRailSettingIdentifiers?.length
            ? data.paymentRailSettingIdentifiers
            : [''],
        });
      })
      .catch((err) => {
        console.error('Error loading payment rail setting:', err);
        onClose();
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, settingId, isEdit]);

  const updateField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const updateIdentifier = (index, value) =>
    setForm((prev) => ({
      ...prev,
      identifiers: prev.identifiers.map((v, i) => (i === index ? value : v)),
    }));

  const addIdentifier = () =>
    setForm((prev) => ({ ...prev, identifiers: [...prev.identifiers, ''] }));

  const removeIdentifier = (index) =>
    setForm((prev) => ({
      ...prev,
      identifiers:
        prev.identifiers.length > 1
          ? prev.identifiers.filter((_, i) => i !== index)
          : prev.identifiers,
    }));

  const handleSave = async () => {
    clearAllErrors();
    const trimmedIdentifiers = form.identifiers
      .map((s) => s.trim())
      .filter(Boolean);
    if (trimmedIdentifiers.length === 0) {
      setBackendErrors({
        errors: [
          {
            path: 'payment_rail_setting_identifiers',
            msg: t('paymentRailSettings.fields.identifierRequired'),
          },
        ],
      });
      return;
    }
    const payload = {
      name: form.name.trim(),
      is_disabled: form.is_disabled,
      payment_rail_setting_identifiers: trimmedIdentifiers,
    };
    setLoading(true);
    try {
      if (isEdit) {
        await PaymentRailSettingsApi.update(organizationId, settingId, payload);
      } else {
        await PaymentRailSettingsApi.create(organizationId, payload);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setBackendErrors(err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{ sx: { minHeight: '70vh' } }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AltRoute color='primary' />
          {t(
            isEdit
              ? 'paymentRailSettings.editTitle'
              : 'paymentRailSettings.createTitle',
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {fetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 2 }}>
            <FormErrorSummary errors={generalErrors} />

            <TextField
              fullWidth
              variant='standard'
              label={t('paymentRailSettings.fields.name')}
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
              inputProps={{ maxLength: 255 }}
              sx={{ mb: 3 }}
            />

            <Box sx={{ mb: 3 }}>
              <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
                {t('paymentRailSettings.fields.identifiers')}
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mb: 1 }}>
                {t('paymentRailSettings.fields.identifierHint')}
              </Typography>
              {form.identifiers.map((value, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    mb: 1,
                  }}>
                  <TextField
                    fullWidth
                    variant='standard'
                    value={value}
                    onChange={(e) => updateIdentifier(index, e.target.value)}
                    placeholder={t(
                      'paymentRailSettings.fields.identifierPlaceholder',
                    )}
                    inputProps={{ maxLength: 255 }}
                    error={
                      !!fieldErrors[
                        `payment_rail_setting_identifiers.${index}`
                      ] ||
                      (index === 0 &&
                        !!fieldErrors.payment_rail_setting_identifiers)
                    }
                    helperText={
                      fieldErrors[
                        `payment_rail_setting_identifiers.${index}`
                      ] ||
                      (index === 0
                        ? fieldErrors.payment_rail_setting_identifiers
                        : undefined)
                    }
                  />
                  <Tooltip
                    title={t('paymentRailSettings.fields.removeIdentifier')}>
                    <span>
                      <IconButton
                        size='small'
                        color='error'
                        disabled={form.identifiers.length <= 1}
                        onClick={() => removeIdentifier(index)}>
                        <Delete fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))}
              <Button
                size='small'
                startIcon={<Add />}
                onClick={addIdentifier}
                sx={{ mt: 1 }}>
                {t('paymentRailSettings.fields.addIdentifier')}
              </Button>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={!form.is_disabled}
                  onChange={(e) =>
                    updateField('is_disabled', !e.target.checked)
                  }
                  color='primary'
                />
              }
              label={t('paymentRailSettings.fields.isEnabled')}
              sx={{ mb: 2 }}
            />

            {isEdit && (
              <>
                <Divider sx={{ my: 3 }} />
                <PaymentRailsSection
                  organizationId={organizationId}
                  paymentRailSettingId={settingId}
                />
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button onClick={onClose} startIcon={<Cancel />} disabled={loading}>
          {t('paymentRailSettings.cancel')}
        </Button>
        <CpdActionButton
          onClick={handleSave}
          loading={loading}
          disabled={fetching}
          startIcon={<Save sx={{ color: 'white' }} />}>
          {loading
            ? t('paymentRailSettings.saving')
            : t('paymentRailSettings.save')}
        </CpdActionButton>
      </DialogActions>
    </Dialog>
  );
}
